import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CalendarClock, Check, Clock, Link2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, errText } from "@/lib/api";
import { MatterCombobox } from "@/components/MatterCombobox";
import { MarkdownField } from "@/components/MarkdownField";
import { Markdown } from "@/components/Markdown";
import { shortDate } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";


export const TodoRow = ({ item, canWrite, onToggle, onDelete, onOpen, day }) => (
  <div
    data-testid={`todo-${item.todo_id}`}
    className={`flex items-start gap-3 border border-border rounded-sm px-3 py-2.5 ${
      item.done ? "opacity-60" : ""
    }`}
    style={
      item.priority === "high" && !item.done
        ? { borderLeft: "5px solid rgb(var(--c-fixed))" }
        : undefined
    }
  >
    <Checkbox
      data-testid={`todo-check-${item.todo_id}`}
      checked={!!item.done}
      disabled={!canWrite}
      onCheckedChange={() => onToggle?.(item)}
      className="mt-1"
    />
    <button
      type="button"
      onClick={() => onOpen?.(item)}
      className="min-w-0 flex-1 text-left"
      data-testid={`todo-open-${item.todo_id}`}
    >
      <span
        className={`block text-base font-semibold leading-snug ${
          item.done ? "line-through" : ""
        }`}
      >
        {item.title}
      </span>
      <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {item.due_date ? (
          <span
            data-testid={`todo-due-${item.todo_id}`}
            className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wider ${
              item.overdue && !item.done ? "text-destructive" : ""
            }`}
          >
            <CalendarClock className="h-3.5 w-3.5" />
            {item.overdue && !item.done ? "Overdue · due " : "Due "}
            {shortDate(item.due_date)}
          </span>
        ) : null}
        {day && item.date < day && !item.done ? (
          <span data-testid={`todo-from-${item.todo_id}`} className="inline-flex items-center gap-1">
            on the list since {shortDate(item.date)}
          </span>
        ) : null}
        {item.priority === "high" ? (
          <span className="inline-flex items-center gap-1 text-fixed font-bold uppercase tracking-wider">
            <AlertTriangle className="h-3.5 w-3.5" /> High
          </span>
        ) : null}
        {item.time_hint ? (
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {item.time_hint}
          </span>
        ) : null}
        {item.matter_title ? (
          <span className="inline-flex items-center gap-1">
            <Link2 className="h-3.5 w-3.5" /> {item.matter_title}
          </span>
        ) : null}
        {item.source === "rollover" ? (
          <span className="inline-flex items-center gap-1">
            rolled from {shortDate(item.rolled_from)}
          </span>
        ) : null}
      </span>
      <Markdown
        text={item.notes}
        className="mt-1.5 text-sm text-muted-foreground"
        testId={`todo-notes-${item.todo_id}`}
      />
    </button>
    {canWrite ? (
      <button
        data-testid={`todo-delete-${item.todo_id}`}
        onClick={() => onDelete?.(item)}
        className="h-9 w-9 shrink-0 grid place-items-center rounded-sm text-destructive hover:bg-destructive/10 transition-colors duration-200"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    ) : null}
  </div>
);

export const TodoPanel = ({ date, variant = "full", onCountChange }) => {
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);
  const [matters, setMatters] = useState([]);
  const [draft, setDraft] = useState({
    title: "",
    priority: "normal",
    time_hint: "",
    matter_id: null,
    due_date: "",
  });
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/todos", { params: { date } });
      setItems(data);
      onCountChange?.(data.filter((t) => !t.done).length);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [date, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (variant === "full") {
      api.get("/matters").then(({ data }) => setMatters(data)).catch(() => setMatters([]));
    }
  }, [variant]);

  const add = async () => {
    if (!draft.title.trim()) return toast.error("Write the to-do first");
    try {
      await api.post("/todos", {
        date,
        due_date: draft.due_date || null,
        title: draft.title.trim(),
        priority: draft.priority,
        time_hint: draft.time_hint.trim() || null,
        matter_id: draft.matter_id || null,
      });
      setDraft({ title: "", priority: "normal", time_hint: "", matter_id: null, due_date: "" });
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const toggle = async (item) => {
    setItems((list) =>
      list.map((i) => (i.todo_id === item.todo_id ? { ...i, done: !i.done } : i)),
    );
    try {
      await api.post(`/todos/${item.todo_id}/toggle`);
      load();
    } catch (e) {
      toast.error(errText(e));
      load();
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/todos/${item.todo_id}`);
      toast.success("To-do removed");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/todos/${editing.todo_id}`, {
        title: editing.title,
        notes: editing.notes || null,
        priority: editing.priority,
        time_hint: editing.time_hint || null,
        matter_id: editing.matter_id || null,
        date: editing.date,
        due_date: editing.due_date || null,
      });
      setEditing(null);
      toast.success("To-do updated");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const open = items.filter((i) => !i.done).length;
  const sorted = useMemo(() => items, [items]);

  if (variant === "compact") {
    return (
      <section className="border border-border rounded-sm p-4" data-testid="todo-panel-compact">
        <div className="flex items-center justify-between gap-3">
          <p className="label-caps inline-flex items-center gap-2">
            <Check className="h-4 w-4" /> To-do · {open} open of {items.length}
          </p>
          {canWrite ? (
            <div className="flex flex-wrap gap-2 justify-end">
              <Input
                data-testid="todo-quick-input"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && add()}
                placeholder="Add a to-do for this day"
                className="rounded-sm h-10 text-sm w-44 sm:w-56"
              />
              <Input
                type="date"
                data-testid="todo-quick-due"
                value={draft.due_date}
                min={date}
                onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
                title="Due by"
                className="rounded-sm h-10 text-sm w-[9.5rem]"
              />
              <Button data-testid="todo-quick-add" onClick={add} className="rounded-full h-10 px-4">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          ) : null}
        </div>
        <div className="mt-3 space-y-2">
          {sorted.length ? (
            sorted.map((item) => (
              <TodoRow
                key={item.todo_id}
                item={item}
                day={date}
                canWrite={canWrite}
                onToggle={toggle}
                onDelete={remove}
              />
            ))
          ) : (
            <p className="text-sm text-muted-foreground">Nothing on the list for this day.</p>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="surface p-6" data-testid="todo-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base md:text-lg font-heading font-semibold">
          {open} open · {items.length} total
        </h2>
      </div>

      {canWrite ? (
        <div className="mt-5 space-y-3" data-testid="todo-add-row">
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
            <div className="sm:col-span-8">
              <Input
                data-testid="todo-title-input"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && add()}
                placeholder="e.g. Draft the written submissions"
                className="rounded-sm h-12 text-base"
              />
            </div>
            <div className="sm:col-span-4 space-y-1">
              <Input
                type="date"
                data-testid="todo-due-input"
                value={draft.due_date}
                min={date}
                onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
                className="rounded-sm h-12 text-base"
              />
              <p className="text-xs text-muted-foreground">
                Due by — leave it blank for no deadline
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
            <div className="sm:col-span-3">
              <Input
                data-testid="todo-time-hint-input"
                value={draft.time_hint}
                onChange={(e) => setDraft({ ...draft, time_hint: e.target.value })}
                placeholder="before 11 AM"
                className="rounded-sm h-12 text-base"
              />
            </div>
            <div className="sm:col-span-3">
              <Select
                value={draft.priority}
                onValueChange={(v) => setDraft({ ...draft, priority: v })}
              >
                <SelectTrigger data-testid="todo-priority-select" className="rounded-sm h-12 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High priority</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-4">
              <MatterCombobox
                matters={matters}
                value={draft.matter_id}
                onChange={(id) => setDraft({ ...draft, matter_id: id })}
                testId="todo-matter-select"
                emptyLabel="No matter"
                height="h-12"
              />
            </div>
            <div className="sm:col-span-2">
              <Button data-testid="todo-add-btn" onClick={add} className="rounded-full h-12 w-full">
                <Plus className="h-5 w-5 mr-1.5" /> Add
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-6 space-y-2">
        {sorted.length ? (
          sorted.map((item) => (
            <TodoRow
              key={item.todo_id}
              item={item}
              day={date}
              canWrite={canWrite}
              onToggle={toggle}
              onDelete={remove}
              onOpen={(i) => canWrite && setEditing({ ...i })}
            />
          ))
        ) : (
          <p className="text-sm text-muted-foreground border border-dashed border-border rounded-sm px-4 py-5">
            Nothing on the list yet.
          </p>
        )}
      </div>

      {editing ? (
        <div className="mt-6 border border-border rounded-sm p-4 space-y-3" data-testid="todo-editor">
          <div className="space-y-2">
            <Label className="label-caps">Title</Label>
            <Input
              data-testid="todo-edit-title"
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              className="rounded-sm h-11 text-base"
            />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Notes</Label>
            <MarkdownField
              value={editing.notes || ""}
              onChange={(v) => setEditing({ ...editing, notes: v })}
              testId="todo-edit-notes"
              minHeight={80}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="label-caps">Shows from</Label>
              <Input
                type="date"
                data-testid="todo-edit-date"
                value={editing.date || ""}
                onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                className="rounded-sm h-11 text-base"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Due by</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  data-testid="todo-edit-due"
                  value={editing.due_date || ""}
                  min={editing.date || undefined}
                  onChange={(e) => setEditing({ ...editing, due_date: e.target.value })}
                  className="rounded-sm h-11 text-base"
                />
                {editing.due_date ? (
                  <Button
                    variant="outline"
                    data-testid="todo-edit-due-clear"
                    onClick={() => setEditing({ ...editing, due_date: "" })}
                    className="rounded-sm h-11 px-3 text-xs shrink-0"
                  >
                    No deadline
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="label-caps">Time hint</Label>
              <Input
                data-testid="todo-edit-time-hint"
                value={editing.time_hint || ""}
                onChange={(e) => setEditing({ ...editing, time_hint: e.target.value })}
                className="rounded-sm h-11 text-base"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Priority</Label>
              <Select
                value={editing.priority}
                onValueChange={(v) => setEditing({ ...editing, priority: v })}
              >
                <SelectTrigger data-testid="todo-edit-priority" className="rounded-sm h-11 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High priority</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Matter</Label>
              <MatterCombobox
                matters={matters}
                value={editing.matter_id}
                onChange={(id) => setEditing({ ...editing, matter_id: id })}
                testId="todo-edit-matter"
                emptyLabel="No matter"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="todo-edit-save" onClick={saveEdit} className="rounded-full h-11 px-5">
              Save
            </Button>
            <Button
              variant="outline"
              data-testid="todo-edit-cancel"
              onClick={() => setEditing(null)}
              className="rounded-full h-11 px-5"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
};
